// Generated by tutorials/record.cjs from tutorials/articles/h73-dining-rooms-and-employees.cjs.
// Do not edit: change the script and run `node tutorials/record.cjs h73 --text-only` (wording) or
// `node tutorials/record.cjs h73` (pictures and clips).
import type { HelpArticle } from '../types';

const article: HelpArticle = {
  slug: 'dining-rooms-and-employees',
  catalogId: 'h73',
  title: 'Dining rooms and employees',
  summary: 'Keep your facility\'s list of dining rooms, with their dining times and tables, and the list of employees who order meals, and see where each is used.',
  audience: 'Administrators and dietary managers who set up the kitchen',
  area: 'kitchen',
  learn: [
    'Add, change and remove a dining room',
    'Set a dining room\'s dining times and tables',
    'Where dining rooms are used: seating assignments and Selective Touch',
    'Add an employee with their Employee ID',
    'Pick the menu employees order from',
  ],
  keywords: [
    'dining room',
    'dining rooms',
    'dining times',
    'seating',
    'seating time',
    'seating assignment',
    'tables',
    'seats',
    'restaurant style',
    'assigned seating',
    'exclude from tally',
    'employee',
    'employees',
    'employee id',
    'staff meals',
    'employee menu',
    'employee meals',
    'employee tray tickets',
    'employee charge summary',
  ],
  steps: [
    {
      id: 'rooms',
      part: 'Add a dining room',
      heading: 'Open Dining Rooms',
      text: 'Select Kitchen in the menu on the left, then Dining Rooms.\n\nEach row is one place where residents eat: its Name and a Description, such as which floor it is on or how many it seats.',
      media: {
        image: {
          src: '/help/dining-rooms-and-employees/rooms.webp',
          width: 1280,
          height: 748,
        },
        alt: 'The Dining Rooms list with Garden Cafe, Main Dining Room and Willow Unit Dining Room and their descriptions.',
      },
    },
    {
      id: 'new-room',
      part: 'Add a dining room',
      heading: 'Add a dining room',
      text: 'Select New at the top left. The New Dining Room box opens.\n\nType the Dining Room Name the way staff say it, such as Sunroom. The name is required. Add a Description if it helps, such as where the room is and how many it seats.',
      media: {
        image: {
          src: '/help/dining-rooms-and-employees/new-room.webp',
          width: 944,
          height: 480,
        },
        clip: {
          src: '/help/dining-rooms-and-employees/new-room.mp4',
          width: 944,
          height: 480,
          durationSec: 6,
        },
        alt: 'The New Dining Room box with Dining Room Name Sunroom and Description Third floor, by the elevators. Seats 12.',
      },
    },
    {
      id: 'save-room',
      part: 'Add a dining room',
      heading: 'Save',
      text: 'Select Save. The room is added to the list.\n\nTo change a room later, double-click it (or select it, then Open), change the name or description, and select Save. To remove one, select it, then More actions (the three dots) and Delete.',
      media: {
        image: {
          src: '/help/dining-rooms-and-employees/save-room.webp',
          width: 1280,
          height: 748,
        },
        clip: {
          src: '/help/dining-rooms-and-employees/save-room.mp4',
          width: 1280,
          height: 748,
          durationSec: 3,
        },
        alt: 'The Dining Rooms list with Sunroom added, described as Third floor, by the elevators. Seats 12.',
      },
    },
    {
      id: 'open-tables',
      part: 'Set dining times and tables',
      heading: 'Open the room in Tables',
      text: 'The dining times and tables of a room are set in Tables. Select Food Data in the menu on the left, then Tables, then Dining Rooms.\n\nDouble-click the room. The Edit Dining Room box opens with the name and description at the top, and the room\'s Dining Times and Tables below them.',
      media: {
        image: {
          src: '/help/dining-rooms-and-employees/open-tables.webp',
          width: 1280,
          height: 1052,
        },
        alt: 'The Edit Dining Room box for Main Dining Room: the name and description, then the Dining Times list and the Tables list.',
      },
    },
    {
      id: 'times',
      part: 'Set dining times and tables',
      heading: 'The dining times',
      text: 'Each row under Dining Times is one seating in this room:\n\n- Type: how residents are seated. Assigned Seating is a set seat at a set time; Assigned Dining is a set time in any seat; the two Restaurant Style choices are any time, any seat. The description of the type shows under it.\n- Description: what staff call the seating, such as 11:30 Seating.\n- Mealtime: Breakfast, Lunch or Dinner.\n- Exclude From Tally: see the notes.\n\nSelect Add for another seating; the red x at the end of a row removes it.',
      media: {
        image: {
          src: '/help/dining-rooms-and-employees/times.webp',
          width: 1280,
          height: 538,
        },
        alt: 'The Dining Times list of Main Dining Room: Assigned Seating at 7:30 for Breakfast, 11:30 and 12:30 for Lunch, and 5:00 for Dinner.',
      },
    },
    {
      id: 'tables',
      part: 'Set dining times and tables',
      heading: 'The tables',
      text: 'Under Tables, list each table in the room: its name, such as Table 1, and its No. Of Seats.\n\nSelect Add for another table; the red x removes one. Seating assignments and Selective Touch pick from these tables and seats.',
      media: {
        image: {
          src: '/help/dining-rooms-and-employees/tables.webp',
          width: 1280,
          height: 674,
        },
        alt: 'The Tables list of Main Dining Room: Table 1 to Table 8, the first six with 8 seats and the last two with 4.',
      },
    },
    {
      id: 'where-rooms',
      part: 'Set dining times and tables',
      heading: 'Where dining rooms are used',
      text: 'Dining rooms, their times and their tables are used in two places:\n\n- A resident\'s record, Seating Assignment: which dining room, dining time, table and seat the resident has, and on which days.\n- Selective Touch, in its Table/Seats modes: staff seat residents and take their orders table by table.',
      textOnly: true,
    },
    {
      id: 'save-tables',
      part: 'Set dining times and tables',
      heading: 'Save',
      text: 'Select Save. The dining times and tables are saved with the room. Cancel leaves everything as it was.',
      media: {
        image: {
          src: '/help/dining-rooms-and-employees/save-tables.webp',
          width: 1280,
          height: 748,
        },
        clip: {
          src: '/help/dining-rooms-and-employees/save-tables.mp4',
          width: 1280,
          height: 748,
          durationSec: 3.1,
        },
        alt: 'The Dining Rooms table in Tables with the Main Dining Room row selected after saving.',
      },
    },
    {
      id: 'employees',
      part: 'Add an employee',
      heading: 'Open Employees',
      text: 'Select Kitchen in the menu on the left, then Employees.\n\nThese are the staff who order meals from the kitchen. Each row has the Last Name, First Name, Employee ID and Full Name.',
      media: {
        image: {
          src: '/help/dining-rooms-and-employees/employees.webp',
          width: 1280,
          height: 748,
        },
        alt: 'The Employees list: nine employees such as Abernathy, Colleen, Employee ID 10422.',
      },
    },
    {
      id: 'new-employee',
      part: 'Add an employee',
      heading: 'Add an employee',
      text: 'Select New. The New Employee box opens.\n\nType the Last Name, the First Name and the Employee ID. The last name and Employee ID are required, and every Employee ID must be different. Use the badge or payroll number staff already know: it is what they enter to order.',
      media: {
        image: {
          src: '/help/dining-rooms-and-employees/new-employee.webp',
          width: 944,
          height: 486,
        },
        clip: {
          src: '/help/dining-rooms-and-employees/new-employee.mp4',
          width: 944,
          height: 486,
          durationSec: 6,
        },
        alt: 'The New Employee box with Last Name Okafor, First Name Grace and Employee ID 10855.',
      },
    },
    {
      id: 'save-employee',
      part: 'Add an employee',
      heading: 'Save',
      text: 'Select Save. The employee is added to the list.\n\nTo change an employee, double-click the row. When someone leaves, select them, then More actions and Delete.',
      media: {
        image: {
          src: '/help/dining-rooms-and-employees/save-employee.webp',
          width: 1280,
          height: 748,
        },
        clip: {
          src: '/help/dining-rooms-and-employees/save-employee.mp4',
          width: 1280,
          height: 748,
          durationSec: 2.9,
        },
        alt: 'The Employees list with Okafor, Grace, Employee ID 10855, added.',
      },
    },
    {
      id: 'employee-menu',
      part: 'Add an employee',
      heading: 'Pick the menu employees order from',
      text: 'The menu employees order from is set in Tables. Select Food Data, then Tables, then Employees.\n\nPick it in Menu, above the list. It saves as soon as you pick it, and Selective Touch and the employee reports use it.',
      media: {
        image: {
          src: '/help/dining-rooms-and-employees/employee-menu.webp',
          width: 1256,
          height: 1080,
        },
        alt: 'Tables, Employees with Menu set to Staff Cafe Menu and the note Select the menu to be used for employee orders, above the list of employees.',
      },
    },
    {
      id: 'where-employees',
      part: 'Add an employee',
      heading: 'Where employees are used',
      text: 'Employees order their meals in Selective Touch: on Employee Menu, they enter their Employee ID and pick from the employee menu.\n\nTheir orders show on the reports in the Employees group: Employee Tray Tickets, Employee Production Tally and Employee - Charge Summary. Served Items has an Employee Served Items view in Analysis.',
      textOnly: true,
    },
  ],
  tips: [
    'The search box at the top finds a dining room by its name or description, and an employee by name or Employee ID.',
    'Name dining rooms the way staff say them, such as Willow Unit Dining Room. The name is what shows on seating assignments and in Selective Touch.',
    'Employee IDs must be unique. Use the badge or payroll number staff already know, since that is what they enter in Selective Touch.',
  ],
  notes: [
    'Changing your dining rooms needs Dining Room Layout level 2 on your role, and deleting one needs level 3. Employees work the same way with the Employee level. If New or Delete is missing, ask your administrator.',
    'Before you delete a dining room, check that no residents are still seated in it. Food Data, Tables, Dining Rooms warns you when a room is still in use.',
    'Exclude From Tally is for facilities that count meals by dining room seating: residents seated at that time are not counted in the Production Tally.',
  ],
  related: ['tables', 'selective-touch', 'selective-touch-setup', 'running-a-report', 'roles-and-access'],
  recorded: '2026-11-03',
};

export default article;
